import { useCallback, useState } from 'react';

const DEFAULT_PAGE_SIZE = 10;

interface UseCursorPaginationOptions {
	pageSize?: number;
}

interface UseCursorPaginationReturn {
	/** Current cursor (undefined for first page) */
	cursor: string | undefined;
	/** Page size */
	pageSize: number;
	/** Current page number (1-based, for display/S.N.) */
	currentPage: number;
	/** Whether there is a previous page */
	hasPrevious: boolean;
	/** Go to next page using the nextCursor from the API response */
	goToNext: (nextCursor: string) => void;
	/** Go to previous page */
	goToPrevious: () => void;
	/** Reset to first page */
	reset: () => void;
}

/**
 * Hook to manage cursor-based pagination state.
 *
 * Maintains a stack of previous cursors so the user can navigate back.
 */
export function useCursorPagination(
	options?: UseCursorPaginationOptions
): UseCursorPaginationReturn {
	const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
	const [cursor, setCursor] = useState<string | undefined>(undefined);
	const [cursorHistory, setCursorHistory] = useState<string[]>([]);

	const currentPage = cursorHistory.length + 1;
	const hasPrevious = cursorHistory.length > 0;

	const goToNext = useCallback(
		(nextCursor: string) => {
			setCursorHistory((prev) => [...prev, cursor ?? '']);
			setCursor(nextCursor);
		},
		[cursor]
	);

	const goToPrevious = useCallback(() => {
		setCursorHistory((prev) => {
			const newHistory = [...prev];
			const previousCursor = newHistory.pop();
			setCursor(previousCursor === '' ? undefined : previousCursor);
			return newHistory;
		});
	}, []);

	const reset = useCallback(() => {
		setCursor(undefined);
		setCursorHistory([]);
	}, []);

	return {
		cursor,
		pageSize,
		currentPage,
		hasPrevious,
		goToNext,
		goToPrevious,
		reset,
	};
}
